import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Menu, X, Calculator, CheckCircle2, AlertCircle, ArrowRight,
  ShieldCheck, Zap, Globe, ChevronDown, Maximize
} from 'lucide-react';
import './padeye.css';

/* ── Images ── */
import imgMaterial from '../../images/Padeye-Material-property.jpg';
import imgGeometry from '../../images/Padeye-Padeyegeometry.png';
import imgShackle from '../../images/padeye/shackle.png';
import imgLoad from '../../images/PadEye-PadeyeLoad.png';
import imgWeld from '../../images/PadEye-weld-size.png';
import imgSling from '../../images/padeye/sling dia.png';
import PadeyeBlackbox from '../../images/PadEye-Blackbox-Side-image.png';
/* ════════════════════════════════════════════════════════════
   UNIT CONVERSION ENGINE (PORTED FROM PADEYE.JS)
   ════════════════════════════════════════════════════════════ */

const FACTORS = {
  stress: { 'MPa': 1, 'Pa': 1e-6 },
  length: { 'mm': 1, 'cm': 10, 'm': 1000, 'in': 25.4 },
  area: { 'mm²': 1, 'cm²': 100 },
  load: { 'MT': 9806.65, 'N': 1, 'kN': 1000 },
  moment: { 'MT-mm': 1 },
  volume: { 'mm³': 1, 'cm³': 1000 }
};

function formatVal(value) {
  if (value == null || isNaN(value)) return "0.00";

  const absVal = Math.abs(value);

  // Use scientific notation only for values > 5 digits
  if (absVal >= 100000) {
    return value.toExponential(2);
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
/* ── RENDER HELPERS ── */
const InputField = ({ label, value, set, unit, setUnit, options, type, isActiveField, onFocus }) => (
  <div className={`field-row ${!isActiveField ? 'field-calculated' : 'field-active'}`}>
    <label className="field-label">{label}</label>
    <div className="input-wrap">
      <input
        type="text"
        inputMode="decimal"
        className="calc-input"
        value={value}
        onFocus={onFocus}
        onChange={(e) => {
          const val = e.target.value;
          // Allow numbers and one decimal point
          if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) {
            set(val);
          }
        }}
      />
      {options ? (
        <select
          className="unit-select"
          value={unit}
          disabled={!isActiveField}
          onChange={(e) => {
            const newUnit = e.target.value;
            if (type && FACTORS[type] && FACTORS[type][unit] && FACTORS[type][newUnit]) {
              const numericVal = parseFloat(value) || 0;
              const metricVal = numericVal * FACTORS[type][unit];
              const converted = metricVal / FACTORS[type][newUnit];
              set(converted.toFixed(4).replace(/\.?0+$/, ''));
            }
            setUnit(newUnit);
          }}
        >
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <span className="unit-badge">{unit}</span>
      )}
    </div>
  </div>
);

/* ── ResultCard: unit-aware display with explicit visible colors ── */
/* ── ResultCard: unit-aware display with explicit visible colors ── */
const ResultCard = ({ label, baseValue, baseType, defaultUnit, isUC, isInversePass, type }) => {
  const [unit, setUnit] = useState(defaultUnit || '');

  const options =
    baseType === 'stress' ? Object.keys(FACTORS.stress) :
      baseType === 'length' ? Object.keys(FACTORS.length) :
        baseType === 'area' ? Object.keys(FACTORS.area) :
          baseType === 'load' ? Object.keys(FACTORS.load) :
            baseType === 'moment' ? Object.keys(FACTORS.moment) :
              baseType === 'volume' ? Object.keys(FACTORS.volume) :
                (defaultUnit ? [defaultUnit] : []);

  let displayValue = baseValue;
  if (baseType && FACTORS[baseType] && FACTORS[baseType][defaultUnit] && FACTORS[baseType][unit]) {
    const metricVal = baseValue * FACTORS[baseType][defaultUnit];
    displayValue = metricVal / FACTORS[baseType][unit];
  }

  // Pass/Fail Logic
  let status = type || 'info';
  if (isUC) {
    if (isInversePass) {
      status = baseValue >= 1 ? 'pass' : 'fail';
    } else {
      status = baseValue <= 1 ? 'pass' : 'fail';
    }
  }

  return (
    <div className={`result-card ${status}`}>
      <div className="result-label">{label}</div>
      <div className="result-main">
        <div className="result-value">
          {typeof baseValue === 'string' ? baseValue : formatVal(displayValue)}
        </div>
        {options.length > 1 ? (
          <select className="result-unit-select" value={unit} onChange={(e) => setUnit(e.target.value)}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <div className="result-unit">{unit}</div>
        )}
      </div>
      {isUC && (
        <div className={`result-status ${status}`}>
          {status === 'pass' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          <span>{status === 'pass' ? 'PASS' : 'FAIL'}</span>
        </div>
      )}
    </div>
  );
};

export default function PadEye() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('material');
  const [openFaq, setOpenFaq] = useState(null);

  /* ── Transaction State (7 Sections Dropdown) ── */
  const [openSection, setOpenSection] = useState(0);

  /* ══ INPUT STATES ══ */
  /* ══ INPUT STATES ══ */
  /* ── Material Properties ── */
  const [smys, setSmys] = useState("300");       // Material Yield Stress (σy)
  const [smysUnit, setSmysUnit] = useState('MPa');
  const [fu, setFu] = useState("410");           // Electrode Tensile Strength (for Weld) (σu)
  const [fuUnit, setFuUnit] = useState('MPa');
  const [E, setE] = useState("200000");          // Modulus of Elasticity (E)
  const [EUnit, setEUnit] = useState('MPa');
  const [nu, setNu] = useState("0.3");           // Poisson's ratio (ν)

  /* ── Padeye Geometry ── */
  const [radius, setRadius] = useState("35");    // Radius of Main Plate (R)
  const [radiusUnit, setRadiusUnit] = useState('mm');
  const [thick, setThick] = useState("12");      // Thickness of Main Plate (t)
  const [thickUnit, setThickUnit] = useState('mm');
  const [holeDia, setHoleDia] = useState("22");  // Diameter of eye pin hole (De)
  const [holeDiaUnit, setHoleDiaUnit] = useState('mm');
  const [cDia, setCDia] = useState("50");        // Diameter of Cheek Plate (Dc)
  const [cDiaUnit, setCDiaUnit] = useState('mm');
  const [cThick, setCThick] = useState("12");    // Thickness of Cheek Plate (tc)
  const [cThickUnit, setCThickUnit] = useState('mm');
  const [height, setHeight] = useState("80");   // Total Height of Pad-eye (H)
  const [heightUnit, setHeightUnit] = useState('mm');
  const [baseL, setBaseL] = useState("115");     // Length of Base Plate (L)
  const [baseLUnit, setBaseLUnit] = useState('mm');

  /* ── Shackle Geometry ── */
  const [sSWL, setSSWL] = useState("3.2");       // Shackle SWL (SWLsh)
  const [sInsideLen, setSInsideLen] = useState("70"); // Shackle Inside Length (Lsh)
  const [sInsideLenUnit, setSInsideLenUnit] = useState('mm');
  const [sJawWidth, setSJawWidth] = useState("27");   // Shackle Jaw Width (Wsh)
  const [sJawUnit, setSJawUnit] = useState('mm');
  const [sPinDia, setSPinDia] = useState("19"); // Shackle Pin Diameter (Dp)
  const [sPinUnit, setSPinUnit] = useState('mm');

  /* ── Sling Geometry ── */
  const [slingDia, setSlingDia] = useState("12");  // Sling Diameter (Dsling)
  const [slingUnit, setSlingUnit] = useState('mm');

  /* ── Design Loads ── */
  const [force, setForce] = useState("1");      // Load on Pad-eye (P)
  const [forceUnit, setForceUnit] = useState('MT');
  const [alpha, setAlpha] = useState("8");       // Angle of Load with Vertical (θ)
  const [beta, setBeta] = useState("5");         // Out-of Plane Angle (φ)
  const [dlf, setDlf] = useState("2");           // Dynamic Load Factor (DLF)

  /* ── Weld Sizes ── */
  const [weldBase, setWeldBase] = useState("6"); // Base Weld Leg Size (tw)
  const [weldBaseUnit, setWeldBaseUnit] = useState('mm');
  const [weldCheek, setWeldCheek] = useState("6"); // Cheek Plate Weld Leg Size (twc)
  const [weldCheekUnit, setWeldCheekUnit] = useState('mm');

  const [results, setResults] = useState(null);

  const runAnalysis = () => {
    // Parse all input strings to numbers
    const f_y = parseFloat(smys) || 0;
    const f_u = parseFloat(fu) || 0;
    const E_u_val = parseFloat(E) || 0;
    const nu_val = parseFloat(nu) || 0.3;

    const rad_val = parseFloat(radius) || 0;
    const tp_val = parseFloat(thick) || 0;
    const de_val = parseFloat(holeDia) || 0;
    const dc_val = parseFloat(cDia) || 0;
    const tc_val = parseFloat(cThick) || 0;
    const hv_val = parseFloat(height) || 0;
    const lb_val = parseFloat(baseL) || 0;

    const dp_val = parseFloat(sPinDia) || 0;
    const lsi_val = parseFloat(sInsideLen) || 0;
    const wj_val = parseFloat(sJawWidth) || 0;
    const sd_val = parseFloat(slingDia) || 0;
    const swl_sh = (parseFloat(sSWL) || 0) * (FACTORS.load['MT']);

    const f_val = parseFloat(force) || 0;
    const alp_val = parseFloat(alpha) || 0;
    const bet_val = parseFloat(beta) || 0;
    const dlf_val = parseFloat(dlf) || 1.0;

    const tw_val = parseFloat(weldBase) || 0;
    const awc_val = parseFloat(weldCheek) || 0;

    const fy = f_y * (FACTORS.stress[smysUnit]);
    const fult = f_u * (FACTORS.stress[fuUnit]);
    const Eu = E_u_val * (FACTORS.stress[EUnit]);

    const R = rad_val * (FACTORS.length[radiusUnit]);
    const tp = tp_val * (FACTORS.length[thickUnit]);
    const de = de_val * (FACTORS.length[holeDiaUnit]);
    const dc = dc_val * (FACTORS.length[cDiaUnit]);
    const tc = tc_val * (FACTORS.length[cThickUnit]);
    const hv = hv_val * (FACTORS.length[heightUnit]);
    const lb = lb_val * (FACTORS.length[baseLUnit]);
    const Rc = dc / 2;

    const dp = dp_val * (FACTORS.length[sPinUnit]);
    const lsi = lsi_val * (FACTORS.length[sInsideLenUnit]);
    const wj = wj_val * (FACTORS.length[sJawUnit]);
    const sd = sd_val * (FACTORS.length[slingUnit]);

    const P = f_val * (FACTORS.load[forceUnit]);
    const pi = Math.PI;
    const a_rad = alp_val * pi / 180;
    const b_rad = bet_val * pi / 180;

    // Load Checks
    const Pz = P * Math.cos(a_rad);
    const Py = P * Math.sin(a_rad) * Math.cos(b_rad);
    const Px = P * Math.sin(a_rad) * Math.sin(b_rad);
    const Pd = P * dlf_val;
    const Pzd = Pz * dlf_val;
    const Pyd = Py * dlf_val;
    const Pxd = (Px * dlf_val) + (0.05 * Pd);

    const tw = tw_val * (FACTORS.length[weldBaseUnit]);
    const twc = awc_val * (FACTORS.length[weldCheekUnit]);

    // Section 1: Allowable Stresses
    const alB = fy * 0.9;
    const alB_in = fy * 0.6;
    const alB_out = fy * 0.75;
    const alT = fy * 0.6;
    const alTp = fy * 0.45;
    const alS = fy * 0.4;
    const alH = fy * 2.5;
    const alW = fult * 0.3;

    // Section 2: Geometry Check
    const mainRadiusOK = R > 1.25 * de;
    const clearPin = de - dp;
    const pinClearOK = clearPin >= 2 || true; // Typically 2mm minimum or verified
    const clearLen = lsi + dp / 2 - R - sd;
    const clearW = wj - tp - 2 * tc;

    // Section 3: Stress Checks at Pin Hole
    const Abe = (pi / 4) * dp * (tp + 2 * tc);
    const sigB = Pd / Abe;
    const ucB = sigB / alB;

    const Asp = 2 * ((R - de / 2) * tp + 2 * (Rc - de / 2) * tc);
    const sigS = Pd / Asp;
    const ucS = sigS / alS;

    const At1p = Asp;
    const sigT1 = Pd / At1p;

    const At2p = (2 * R + pi * Rc / 2) * tp;
    const sigT2 = Pd / At2p;
    const sigT_max = Math.max(sigT1, sigT2);
    const ucTp = sigT_max / alTp;

    const p_line = Pd / (tp + 2 * tc);
    const sigH = Math.sqrt((p_line * Eu * (de - dp)) / (pi * (1 - nu_val * nu_val) * de * dp));
    const ucH = sigH / alH;

    // Section 4: Stress Checks At Base Plate
    const Atb = lb * tp;
    const sigT_base = Pzd / Atb;
    const ucT_base = sigT_base / alT;

    const h_lev = hv - R;
    const e_lev = R - (lb / 2);
    const Mdxx_Nmm = (Pyd * h_lev) + (Pzd * e_lev);
    const Zbxx = tp * lb * lb / 6;
    const sigBbx = Mdxx_Nmm / Zbxx;


    const Mdyy_Nmm = Pxd * h_lev;
    const Zbyy = lb * tp * tp / 6;
    const sigBby = Mdyy_Nmm / Zbyy;

    const Asb = tp * lb;
    const tauSby = Pyd / Asb;
    const tauSbx = Pxd / Asb;

    const sigVM = Math.sqrt(Math.pow(sigT_base + sigBbx + sigBby, 2) + 3 * (Math.pow(tauSbx, 2) + Math.pow(tauSby, 2)));
    const aisc_h21 = (sigT_base / alT) + (sigBbx / alB_in) + (sigBby / alB_out);

    // Section 5: Weld Stress Check of Base Weld
    const twt = tw / Math.sqrt(2);
    const Lw = 2 * lb + 2 * tp;

    const Asbw = twt * Lw;
    const tauSbwy = Pyd / Asbw;
    const tauSbwx = Pxd / Asbw;

    const Atbw = twt * Lw;
    const sigTbw = Pzd / Atbw;

    const Zwbxx = (tp * lb + Math.pow(lb, 2) / 3) * tw;
    const Zwbyy = (tp * lb + Math.pow(tp, 2) / 3) * tw;

    const sigBbwx = Mdxx_Nmm / Zwbxx;
    const sigBbwy = Mdyy_Nmm / Zwbyy;

    const Mdxx = Mdxx_Nmm / 9810;
    const Mdyy = Mdyy_Nmm / 9810;

    const sigCbw = Math.sqrt(Math.pow(sigTbw + sigBbwx + sigBbwy, 2) + Math.pow(tauSbwx, 2) + Math.pow(tauSbwy, 2));
    const ucW = sigCbw / alW;

    // Section 6: Weld Check At Cheek Plate Weld
    const twtc = twc / Math.sqrt(2);
    const Lwc = (pi * dc) / 2;
    const Awc = Lwc * twtc;
    const Pdc = Pd * tc / (tp + 2 * tc);
    const sigWc = Pdc / Awc;
    const ucWc = sigWc / alW;

    const shackleSWLOK = swl_sh >= Pd;
    const slingClearOK = clearLen >= 0 && clearW >= 0;

    // 14 Final Check Conditions
    const shackleClearanceOK = clearPin > 2 && clearPin < 6;
    const bearingStressOK = sigB < alB;
    const shearStressPinHoleOK = sigS < alS;
    const tensileStressPinHoleOK = sigT_max < alTp;
    const hertzStressOK = sigH < alH;
    const tensileStressBaseOK = sigT_base < alT;
    const bendingStressBaseXOK = sigBbx < alB_in;
    const bendingStressBaseYOK = sigBby < alB_out;
    const shearStressBaseYOK = tauSby < alS;
    const shearStressBaseXOK = tauSbx < alS;
    const combinedStressAISC_OK = aisc_h21 < 1;
    const combinedStressBaseWeldOK = sigCbw < alW;
    const cheekPlateWeldStressOK = sigWc < alW;

    setResults({
      alB, alB_in, alB_out, alT, alTp, alS, alH, alW,
      Pz, Py, Px, Pd, Pzd, Pyd, Pxd,
      clearPin, clearLen, clearW, pinClearOK,
      twtc, Lwc, Awc, Pdc, sigWc, ucWc,
      Abe, sigB, ucB, Rc, Asp, sigS, ucS, At1p, sigT1, At2p, sigT2, ucTp, p_line, sigH, ucH,
      Atb, Asb, sigT_base, ucT_base, Zbxx, Zbyy, h_lev, e_lev, Mdxx, Mdyy,
      sigBbx, sigBby, tauSbx, tauSby, sigVM, aisc_h21,
      tw, twt, Lw, Asbw, Atbw, Zwbxx, Zwbyy, tauSbwy, tauSbwx, sigTbw, sigBbwx, sigBbwy, sigCbw, ucW,
      mainRadiusOK, shackleSWLOK, slingClearOK, swl_sh,
      shackleClearanceOK, bearingStressOK, shearStressPinHoleOK, tensileStressPinHoleOK,
      hertzStressOK, tensileStressBaseOK, bendingStressBaseXOK, bendingStressBaseYOK,
      shearStressBaseYOK, shearStressBaseXOK, combinedStressAISC_OK, combinedStressBaseWeldOK,
      cheekPlateWeldStressOK
    });

    setOpenSection(0);
    const el = document.getElementById('calc-results');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const resetAll = () => {
    setResults(null);
    setOpenSection(0);
  };

  /* ══ RENDER HELPERS ══ */


  return (
    <div className="padeye-landing">
      <Helmet>
        <title>Pad Eye Calculator | Free Padeye Design & Lifting Lug Analysis Tool</title>
        <meta name="description" content="Free online pad eye calculator for structural and offshore lifting design. Check bearing stress, tensile stress, shear, bending, weld capacity and shackle compatibility. Based on AISC, DNV, and NORSOK standards." />
        <meta name="keywords" content="pad eye calculator, padeye calculator, lifting lug calculator, padeye design, shackle compatibility, offshore lifting, bearing stress, tensile stress, weld capacity, DNV padeye, NORSOK R-002, AISC BTH-1, cheek plate design, lifting point analysis, seafastening" />
      </Helmet>

      {/* HERO */}
      <br />
      <br />
      <br />
      <br />
      <header className="hero padeye">
        <div className="hero-pattern"></div>

        <h1>Pad Eye <em>Calculator</em><br />— Free Lifting Lug Design Tool</h1>
        <p className="hero-lead">Instantly verify padeye and lifting lug designs for bearing stress, tensile capacity, shear, bending, and weld strength. Built for structural, offshore, and marine engineers.</p>
        <div className="hero-badges">
          <span className="badge blue"><ShieldCheck size={12} /> AISC BTH-1</span>
          <span className="badge blue">DNV-ST-E273</span>
          <span className="badge blue">NORSOK R-002</span>
          <span className="badge green"><Zap size={12} /> Free to Use</span>
          <span className="badge">Bearing Stress</span>
          <span className="badge">Cheek Plate Design</span>
          <span className="badge">Weld Verification</span>
          <span className="badge">Shackle Compatibility</span>
          <span className="badge amber">Offshore · Marine · Structural</span>
        </div>
      </header>

      <main className="page-body">


        {/* CALCULATOR SHELL INTEGRATION */}
        <section id="calculator" className="calc-shell">
          <div className="calc-header">
            <div>
              <h2>Pad Eye Design Tool</h2>
              <p>Enter parameters below. Results update after clicking Analyze.</p>
            </div>
            {!results ? (
              <button className="calc-btn" onClick={runAnalysis}>Run Analysis <Zap size={16} /></button>
            ) : (
              <button className="calc-btn amber" onClick={resetAll}>Reset Fields <AlertCircle size={16} /></button>
            )}
          </div>

          <div className="calc-3col">
            <div className="calc-nav">
              <button className={`calc-nav-item ${activeTab === 'material' ? 'active' : ''}`} onClick={() => setActiveTab('material')}>
                🧱 Material Properties
              </button>
              <button className={`calc-nav-item ${activeTab === 'geometry' ? 'active' : ''}`} onClick={() => setActiveTab('geometry')}>
                📐 Padeye Geometry
              </button>
              <button className={`calc-nav-item ${activeTab === 'shackle' ? 'active' : ''}`} onClick={() => setActiveTab('shackle')}>
                🔗 Shackle Geometry
              </button>
              <button className={`calc-nav-item ${activeTab === 'sling' ? 'active' : ''}`} onClick={() => setActiveTab('sling')}>
                🧶 Sling Geometry
              </button>
              <button className={`calc-nav-item ${activeTab === 'load' ? 'active' : ''}`} onClick={() => setActiveTab('load')}>
                ⬇️ Design Loads
              </button>
              <button className={`calc-nav-item ${activeTab === 'weld' ? 'active' : ''}`} onClick={() => setActiveTab('weld')}>
                ⚡ Weld Sizes
              </button>
            </div>

            <div className="calc-img-pane">
              <div className="calc-img-wrap">
                {activeTab === 'material' && <img src={imgMaterial} alt="Material" />}
                {activeTab === 'geometry' && <img src={imgGeometry} alt="Geometry" />}
                {activeTab === 'shackle' && <img src={imgShackle} alt="Shackle" />}
                {activeTab === 'sling' && <img src={imgSling} alt="Sling" />}
                {activeTab === 'load' && <img src={imgLoad} alt="Load" />}
                {activeTab === 'weld' && <img src={imgWeld} alt="Weld" />}
              </div>
              <div className="calc-img-caption">
                {activeTab === 'material' ? 'Material Strength Reference' :
                  activeTab === 'geometry' ? 'Padeye Geometric Parameters' :
                    activeTab === 'shackle' ? 'Shackle Interface Dimensions' :
                      activeTab === 'sling' ? 'Sling & Grommet Diameter' :
                        activeTab === 'load' ? 'Applied Design Load Vectors' :
                          activeTab === 'weld' ? 'Weld Path & Sizing Reference' : 'Engineering Reference Diagram'}
              </div>
            </div>

            <div className="calc-inputs-pane">
              {activeTab === 'material' && (
                <>
                  <InputField label="Material Yield Stress (σy)" value={smys} set={setSmys} unit={smysUnit} setUnit={setSmysUnit} options={['MPa', 'Pa']} type="stress" isActiveField={true} />
                  <InputField label="Electrode Tensile Strength (σu)" value={fu} set={setFu} unit={fuUnit} setUnit={setFuUnit} options={['MPa', 'Pa']} type="stress" isActiveField={true} />
                  <InputField label="Modulus of Elasticity (E)" value={E} set={setE} unit={EUnit} setUnit={setEUnit} options={['MPa', 'Pa']} type="stress" isActiveField={true} />
                  <InputField label="Poisson's Ratio (ν)" value={nu} set={setNu} unit="—" isActiveField={true} />
                </>
              )}
              {activeTab === 'geometry' && (
                <>
                  <InputField label="Radius of Main Plate (R)" value={radius} set={setRadius} unit={radiusUnit} setUnit={setRadiusUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Thickness of Main Plate (t)" value={thick} set={setThick} unit={thickUnit} setUnit={setThickUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Diameter of eye pin hole (De)" value={holeDia} set={setHoleDia} unit={holeDiaUnit} setUnit={setHoleDiaUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Diameter of Cheek Plate (Dc)" value={cDia} set={setCDia} unit={cDiaUnit} setUnit={setCDiaUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Thickness of Cheek Plate (tc)" value={cThick} set={setCThick} unit={cThickUnit} setUnit={setCThickUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Total Height of Pad-eye (H)" value={height} set={setHeight} unit={heightUnit} setUnit={setHeightUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Length of Base Plate (L)" value={baseL} set={setBaseL} unit={baseLUnit} setUnit={setBaseLUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                </>
              )}
              {activeTab === 'shackle' && (
                <>
                  <InputField label="Shackle SWL (SWLsh)" value={sSWL} set={setSSWL} unit="MT" isActiveField={true} />
                  <InputField label="Shackle Pin Diameter (Dp)" value={sPinDia} set={setSPinDia} unit={sPinUnit} setUnit={setSPinUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Shackle Inside Length (Lsh)" value={sInsideLen} set={setSInsideLen} unit={sInsideLenUnit} setUnit={setSInsideLenUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Shackle Jaw Width (Wsh)" value={sJawWidth} set={setSJawWidth} unit={sJawUnit} setUnit={setSJawUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                </>
              )}
              {activeTab === 'sling' && (
                <>
                  <InputField label="Sling Diameter (Dsling)" value={slingDia} set={setSlingDia} unit={slingUnit} setUnit={setSlingUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                </>
              )}
              {activeTab === 'load' && (
                <>
                  <InputField label="Load on Pad-eye (P)" value={force} set={setForce} unit={forceUnit} setUnit={setForceUnit} options={['MT', 'N', 'kN']} type="load" isActiveField={true} />
                  <InputField label="Angle of Load with Vertical (θ)" value={alpha} set={setAlpha} unit="deg" isActiveField={true} />
                  <InputField label="Out-of Plane Angle (φ)" value={beta} set={setBeta} unit="deg" isActiveField={true} />
                  <InputField label="Dynamic Load Factor (DLF)" value={dlf} set={setDlf} unit="—" isActiveField={true} />
                </>
              )}
              {activeTab === 'weld' && (
                <>
                  <InputField label="Base Weld Leg Size (tw)" value={weldBase} set={setWeldBase} unit={weldBaseUnit} setUnit={setWeldBaseUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                  <InputField label="Cheek Plate Weld Leg Size (twc)" value={weldCheek} set={setWeldCheek} unit={weldCheekUnit} setUnit={setWeldCheekUnit} options={['mm', 'cm', 'm', 'in']} type="length" isActiveField={true} />
                </>
              )}
            </div>
          </div>

          <div id="calc-results" className="calculator-results-wrapper">
            {results && (
              <div className="results-dropdown-container">
                <h3 className="calc-section-title">Analysis Results</h3>

                {/* SECTION 1 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 0 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 0 ? null : 0)}>
                    <span>1. Allowable Stresses & Design Loads</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 0 ? 'open' : ''}`}>
                    <div className="results-grid">
                      Allowable Stresses:

                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Allowable Bearing Stress" baseValue={results.alB} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Bending Stress (In-Plane)" baseValue={results.alB_in} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Bending Stress (Out-of-Plane)" baseValue={results.alB_out} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Tensile Stress" baseValue={results.alT} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Tensile Stress at Pin Hole" baseValue={results.alTp} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Shear Stress" baseValue={results.alS} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Hertz Stress (at Pin Hole)" baseValue={results.alH} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Allowable Weld Stress" baseValue={results.alW} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Design Loads:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Load in Vertical Direction - Z direction (Pz)" baseValue={results.Pz} baseType="load" defaultUnit="N" />
                      <ResultCard label="Load in Horizontal Direction - Y direction (Py)" baseValue={results.Py} baseType="load" defaultUnit="N" />
                      <ResultCard label="Out-of-plane Load (Lateral Load) - X direction (Px)" baseValue={results.Px} baseType="load" defaultUnit="N" />
                      <ResultCard label="Pad-eye Design Load (Pd)" baseValue={results.Pd} baseType="load" defaultUnit="N" />
                      <ResultCard label="Design Load in Vertical Direction (Z-direction) (Pzd)" baseValue={results.Pzd} baseType="load" defaultUnit="N" />
                      <ResultCard label="Design Load in Horizontal Direction (Y-direction) (Pyd)" baseValue={results.Pyd} baseType="load" defaultUnit="N" />
                      <ResultCard label="Design Load Out-of-Plane (Lateral, X-direction)² (Pxd)" baseValue={results.Pxd} baseType="load" defaultUnit="N" />
                    </div>
                  </div>
                </div>

                {/* SECTION 2 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 1 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 1 ? null : 1)}>
                    <span>2. GEOMETRY CHECK</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 1 ? 'open' : ''}`}>
                    <div className="results-grid">
                      <ResultCard label="Clearance between pin hole dia and pin dia (δD)" baseValue={results.clearPin} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Length Clearance of Shackle (δL)" baseValue={results.clearLen} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Width Clearance of Shackle (δW)" baseValue={results.clearW} baseType="length" defaultUnit="mm" />
                    </div>
                  </div>
                </div>

                {/* SECTION 3 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 2 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 2 ? null : 2)}>
                    <span>3. STRESS CHECKS AT PIN HOLE</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 2 ? 'open' : ''}`}>
                    <div className="results-grid">
                      Bearing Stress Check at Pin Hole:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>

                      <ResultCard label="Bearing Area (Abe)" baseValue={results.Abe} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Bearing Stress Actual (Act)" baseValue={results.sigB} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Shear Stress Check at Pin Hole:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>

                      <ResultCard label="Radius of Cheek Plate (Rc)" baseValue={results.Rc} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Shear Area at Pin Hole (Asp)" baseValue={results.Asp} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Shear Stress at pin hole (Act)" baseValue={results.sigS} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Tensile Stress Check at Pin Hole:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Tensile Area for section A-A" baseValue={results.At1p} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Tensile Stress at pin hole (Act) at section A-A" baseValue={results.sigT1} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Tensile Area for section B-B" baseValue={results.At2p} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Tensile Stress at pin hole (Act) at section B-B" baseValue={results.sigT2} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Hertz/Contact Stress Check at Pin Hole:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Design Load per Unit Length (p)" baseValue={results.p_line} baseType="load" defaultUnit="N" />
                      <ResultCard label="Hertz Stress at Pin Hole (Act)" baseValue={results.sigH} baseType="stress" defaultUnit="MPa" />
                    </div>
                  </div>
                </div>

                {/* SECTION 4 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 3 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 3 ? null : 3)}>
                    <span>4. STRESS CHECKS AT BASE PLATE</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 3 ? 'open' : ''}`}>
                    <div className="results-grid">
                      Bearing Stress Check at Pin Hole:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Tensile Area (Atb)" baseValue={results.Atb} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Tensile Stress at Base (Act)" baseValue={results.sigT_base} baseType="stress" defaultUnit="MPa" isUC={false} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Bending Stress Check:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Moment lever for Horizontal/Lateral Force (h)" baseValue={results.h_lev} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Moment lever for Vertical Force (e)" baseValue={results.e_lev} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Total Design Moment about X-axis (In-plane) (Mdxx)" baseValue={results.Mdxx} baseType="moment" defaultUnit="MT-mm" />
                      <ResultCard label="Section Modulus of Base about X-axis (Zbxx)" baseValue={results.Zbxx} baseType="volume" defaultUnit="mm³" />
                      <ResultCard label="Bending Stress (Act) - about X-axis (In-plane) (σbbx(act))" baseValue={results.sigBbx} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Total Design Moment about Y-axis (Out-of Plane) (Mdyy)" baseValue={results.Mdyy} baseType="moment" defaultUnit="MT-mm" />
                      <ResultCard label="Section Modulus of Base about Y-axis (Zbyy)" baseValue={results.Zbyy} baseType="volume" defaultUnit="mm³" />
                      <ResultCard label="Bending Stress (Act) - about Y-axis (Out-of-Plane) (σbby(act))" baseValue={results.sigBby} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Shear Stress Check at Base:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Shear Area at Base (Horizontal)	(Asb)" baseValue={results.Asb} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Shear Stress at Base (Act) - Horizontal (Y direction)	(τsby(act))" baseValue={results.tauSby} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Shear Stress at Base (Act) - Lateral (X direction)	(τsbx(act))" baseValue={results.tauSbx} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Von Mises Stress Check at Base:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Von Mises Stress at Base (Act)	(σvb(act))" baseValue={results.sigVM} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Combined Stress Check as per AISC H2-1:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="AISC H2-1 Unity Check" baseValue={results.aisc_h21} />
                    </div>
                  </div>
                </div>

                {/* SECTION 5 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 4 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 4 ? null : 4)}>
                    <span>5. WELD STRESS CHECK OF BASE WELD</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 4 ? 'open' : ''}`}>
                    <div className="results-grid">
                      Tensile Stress Check:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Throat Thickness (twt)" baseValue={results.twt} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Total Weld Length (Lw)" baseValue={results.Lw} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Moment lever for Horizontal/Lateral Force (h)" baseValue={results.h_lev} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Moment lever for Vertical Force (e)" baseValue={results.e_lev} baseType="length" defaultUnit="mm" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Shear Stress at Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Shear Area of Base Weld (Asbw)" baseValue={results.Asbw} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Shear Stress at Base Weld (Act) - Horizontal (Y -direction) (τsbwy)" baseValue={results.tauSbwy} baseType="stress" defaultUnit="MPa" />
                      <ResultCard label="Shear Stress at Base Weld (Act) - Lateral (X - direction) (τsbwx)" baseValue={results.tauSbwx} baseType="stress" defaultUnit="MPa" />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Tensile Stress at Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Tensile Area at Base (Atbw)" baseValue={results.Asbw} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Tensile Stress at Base Weld (Act)" baseValue={results.sigTbw} baseType="stress" defaultUnit="MPa" isUC={false} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Section Modulus of Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Section Modulus of Weld about X-axis (Zwbxx)" baseValue={results.Zwbxx} baseType="volume" defaultUnit="mm³" />
                      <ResultCard label="Section Modulus of Weld about Y-axis (Zwbyy)" baseValue={results.Zwbyy} baseType="volume" defaultUnit="mm³" />
                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Bending Stress Check at Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Total Design Moment about X-axis (In-plane)	(Mdxx)" baseValue={results.Mdxx} baseType="moment" defaultUnit="MT-mm" />
                      <ResultCard label="Bending Stress at Base Weld (Act) - about X Axis (In Plane)	(σbbwx(act))" baseValue={results.sigBbwx} baseType="stress" defaultUnit="MPa" isUC={false} />
                      <ResultCard label="Total Design Moment about Y-axis (Out-of Plane)	(Mdyy)" baseValue={results.Mdyy} baseType="moment" defaultUnit="MT-mm" />
                      <ResultCard label="Bending Stress (Act) - about Y-axis (Out-of-Plane)	(σbby(act))" baseValue={results.sigBbwy} baseType="stress" defaultUnit="MPa" isUC={false} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Total Stress Check at Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Total Stress at Base Weld (Act)" baseValue={results.sigCbw} baseType="stress" defaultUnit="MPa" />

                    </div>
                  </div>
                </div>

                {/* SECTION 6 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 5 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 5 ? null : 5)}>
                    <span>6. WELD CHECK AT CHEEK PLATE WELD</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 5 ? 'open' : ''}`}>
                    <div className="results-grid">
                      Total Stress Check at Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Throat Thickness (twt)" baseValue={results.twt} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Weld Length (Lwc)" baseValue={results.Lwc} baseType="length" defaultUnit="mm" />
                      <ResultCard label="Weld Area at Cheek Plate (Awc)" baseValue={results.Awc} baseType="area" defaultUnit="mm²" />
                      <ResultCard label="Design Load on one Cheek Plate (Pdc)" baseValue={results.Pdc} baseType="load" defaultUnit="N" />
                      <ResultCard label="Weld Stress at Cheek Weld (Act)" baseValue={results.sigWc} baseType="stress" defaultUnit="MPa" uc={results.ucWc} />

                    </div>
                  </div>
                </div>

                {/* SECTION 7 Dropdown */}
                <div className={`faq-item result-acc ${openSection === 6 ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenSection(openSection === 6 ? null : 6)}>
                    <span>7. FINAL CHECKS</span>
                    <ChevronDown className="chevron" size={16} />
                  </button>
                  <div className={`faq-a ${openSection === 6 ? 'open' : ''}`}>
                    <div className="results-grid">
                      Geometry check:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Main Plate Radius" baseValue={results.mainRadiusOK ? 'OK' : 'NOT OK'} type={results.mainRadiusOK ? 'pass' : 'fail'} />
                      <ResultCard label="Shackle Clearance Checks" baseValue={results.shackleClearanceOK ? 'OK' : 'NOT OK'} type={results.shackleClearanceOK ? 'pass' : 'fail'} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Stress Checks at Pin Hole:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Bearing Stress Check at Pin Hole" baseValue={results.bearingStressOK ? 'OK' : 'NOT OK'} type={results.bearingStressOK ? 'pass' : 'fail'} />
                      <ResultCard label="Shear Stress Check at Pin Hole" baseValue={results.shearStressPinHoleOK ? 'OK' : 'NOT OK'} type={results.shearStressPinHoleOK ? 'pass' : 'fail'} />
                      <ResultCard label="Tensile Stress Check at Pin Hole" baseValue={results.tensileStressPinHoleOK ? 'OK' : 'NOT OK'} type={results.tensileStressPinHoleOK ? 'pass' : 'fail'} />
                      <ResultCard label="Hertz/Contact Stress Check at Pin Hole" baseValue={results.hertzStressOK ? 'OK' : 'NOT OK'} type={results.hertzStressOK ? 'pass' : 'fail'} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Stress Check Base Plate:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Tensile Stress Check at Base" baseValue={results.tensileStressBaseOK ? 'OK' : 'NOT OK'} type={results.tensileStressBaseOK ? 'pass' : 'fail'} />
                      <ResultCard label="Bending Stress Check at Base (X-axis)" baseValue={results.bendingStressBaseXOK ? 'OK' : 'NOT OK'} type={results.bendingStressBaseXOK ? 'pass' : 'fail'} />
                      <ResultCard label="Bending Stress Check at Base (Y-axis)" baseValue={results.bendingStressBaseYOK ? 'OK' : 'NOT OK'} type={results.bendingStressBaseYOK ? 'pass' : 'fail'} />
                      <ResultCard label="Shear Stress Check at Base (Y direction)" baseValue={results.shearStressBaseYOK ? 'OK' : 'NOT OK'} type={results.shearStressBaseYOK ? 'pass' : 'fail'} />
                      <ResultCard label="Shear Stress Check at Base (X direction)" baseValue={results.shearStressBaseXOK ? 'OK' : 'NOT OK'} type={results.shearStressBaseXOK ? 'pass' : 'fail'} />
                      <ResultCard label="Combined Stress Check as per AISC H2-1" baseValue={results.combinedStressAISC_OK ? 'OK' : 'NOT OK'} type={results.combinedStressAISC_OK ? 'pass' : 'fail'} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Weld Stress Check of Base Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Combined Stress Check at Base" baseValue={results.combinedStressBaseWeldOK ? 'OK' : 'NOT OK'} type={results.combinedStressBaseWeldOK ? 'pass' : 'fail'} />

                      <div className="res-sep" style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', margin: '10px 0' }}></div>
                      Weld Check at Cheek Plate Weld:
                      <div className="res-sep" style={{ gridColumn: '1 / -1' }}></div>
                      <ResultCard label="Stress Check at Cheek Plate Weld" baseValue={results.cheekPlateWeldStressOK ? 'OK' : 'NOT OK'} type={results.cheekPlateWeldStressOK ? 'pass' : 'fail'} />
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </section>



        {/* WHAT IS A PADEYE */}
        <section id="what-is-padeye">
          <div className="section-label">Definition & Overview</div>
          <h2 className="section-title">What Is a Pad Eye (Padeye)?</h2>
          <div className="intro-grid">
            <div className="section-body">
              <p>A <strong>pad eye</strong> is a structural steel lifting lug consisting of a circular plate with a central pinhole, welded to a structure and used to attach lifting slings, chains, or shackles.</p>
              <p>Pad eyes are used across a wide range of industries — <strong>offshore oil & gas</strong> for subsea module lifting, <strong>shipbuilding</strong> for crane lift points, and <strong>structural engineering</strong> for permanent lift points on steel fabrications.</p>
              <p>Unlike a generic hole in a plate, a properly designed padeye accounts for multiple simultaneous failure modes and is matched geometrically to a specific shackle.</p>
            </div>
            <div className="anatomy-wrap" role="img" aria-label="Pad eye anatomy diagram">
              <div className="anatomy-title">Padeye Anatomy</div>
              <img
                src={PadeyeBlackbox}
                alt="Placeholder"
                className="img-fluid"
                style={{ width: '100%', maxWidth: '340px', display: 'block', margin: '0 auto' }}
              />
            </div>
          </div>
          <div className="tip-box">
            <strong>Design tip:</strong> For offshore lifts where the sling angle introduces an in-plane load component, an <em>asymmetric</em> geometry distributes stress more efficiently.
          </div>
        </section>

        <hr className="section-sep" />

        {/* FAILURE MODES */}
        <section id="failure-modes">
          <div className="section-label">Structural Checks</div>
          <h2 className="section-title">Failure Modes & Stress Checks</h2>
          <div className="failure-grid">
            <div className="failure-card red">
              <div className="failure-icon red"><CheckCircle2 size={18} /></div>
              <div className="failure-name">Bearing Stress</div>
              <div className="failure-desc">Contact stress between shackle pin and hole. Critical when pin-to-hole clearance is large.</div>
              <div className="formula-pill">σ_b = F / (d_pin × t_eff)</div>
            </div>
            <div className="failure-card amber">
              <div className="failure-icon amber"><CheckCircle2 size={18} /></div>
              <div className="failure-name">Tensile Stress</div>
              <div className="failure-desc">Tension across the net section of the eye plate. Acts through the main plate.</div>
              <div className="formula-pill">σ_t = F / (2 × a × t_p)</div>
            </div>
            <div className="failure-card green">
              <div className="failure-icon green"><CheckCircle2 size={18} /></div>
              <div className="failure-name">Shear Stress</div>
              <div className="failure-desc">Shear through the plane of maximum shear in the eye. Dominant in thin plates.</div>
              <div className="formula-pill">τ = F / (2 × R × t_p)</div>
            </div>
            <div className="failure-card blue">
              <div className="failure-icon blue"><CheckCircle2 size={18} /></div>
              <div className="failure-name">Bending Stress</div>
              <div className="failure-desc">Analogous to a bar loaded at mid-span. Typically the dominant failure mode.</div>
              <div className="formula-pill">σ_M = M / Z_eff</div>
            </div>
          </div>
          <div className="stress-callout">
            <div className="callout-item">
              <div className="callout-pct">40%</div>
              <div className="callout-label">of SMYS for design loads<br /><strong>&lt; 25 tonnes</strong></div>
            </div>
            <div className="callout-item">
              <div className="callout-pct">50%</div>
              <div className="callout-label">of SMYS for design loads<br /><strong>25 – 100 tonnes</strong></div>
            </div>
            <div className="callout-item">
              <div className="callout-pct">67%</div>
              <div className="callout-label">of SMYS for design loads<br /><strong>&gt; 100 tonnes</strong></div>
            </div>
          </div>
        </section>

        <hr className="section-sep" />

        {/* DESIGN STEPS */}
        <section id="design-steps">
          <div className="section-label">Using the Calculator</div>
          <h2 className="section-title">How to Use the OOK Pad Eye Calculator</h2>
          <div className="section-body">
            <p>The OOK Pad Eye Calculator guides you through the full design workflow in a single session. Follow these steps to obtain a complete stress-verified padeye design with shackle compatibility.</p>
          </div>
          <div className="padeye-steps-wrap">
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Define the design load (F)</div>
                <div className="step-body">Enter the applied load including all load factors — Dynamic Amplification Factor (DAF), Safety Factor (SF), and any project-specific environmental or acceleration factors. For offshore applications, DAF typically ranges from 1.05 to 1.30 depending on sea state and lift height. For seafastening, vessel motion acceleration factors must also be applied. The calculator treats F as the fully-factored design load.</div>
              </div>
            </div>
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Select or input padeye geometry</div>
                <div className="step-body">Specify the outer radius (R), pin hole diameter (d_h), main plate thickness (t_p), and cheek plate thickness (t) if applicable. The outer radius should be at least equal to the pin hole radius. Typical proportions: outer radius = 2–3× hole radius; plate width at base = 3–4× hole diameter. You can also select a shackle from the built-in database to auto-populate compatible geometry.</div>
              </div>
            </div>
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Input material properties</div>
                <div className="step-body">Enter the SMYS (yield strength) of the padeye plate material. Common grades include S355 (355 MPa), S275 (275 MPa), A36 (250 MPa), and high-strength grades such as S690 for heavy-lift applications. The calculator uses SMYS to compute allowable stress limits per the selected design code.</div>
              </div>
            </div>
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Set eye clearance (C) and cheek plate parameters</div>
                <div className="step-body">Eye clearance is the total gap between the cheek plates and the shackle eyes. Convention is C = 10% of the width between shackle eyes (w_E) or 6 mm (¼″), whichever is smaller. If no cheek plates are used, set cheek thickness t = 0 and the calculator assumes a full-width main plate between the shackle eyes minus the clearance.</div>
              </div>
            </div>
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Review all stress utilisation ratios</div>
                <div className="step-body">The results panel shows Unity Check (UC = actual stress / allowable stress) for each failure mode. UC ≤ 1.0 means the check passes. The overall design passes only if all stress checks pass simultaneously. If any UC exceeds 1.0, increase plate thickness, reduce pin hole diameter, or add / increase cheek plates.</div>
              </div>
            </div>
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Verify shackle compatibility</div>
                <div className="step-body">The calculator checks that the padeye hole diameter matches the selected shackle pin, that the plate thickness at the hole is at least 75% of the shackle inside jaw width, and that the eye clearance is within tolerance. For padeyes with out-of-plane loading exceeding 10%, the shackle pin should be at least 94% of the padeye hole diameter.</div>
              </div>
            </div>
            <div className="padeye-step-item">
              <div className="step-content">
                <div className="step-title">Apply lateral load check</div>
                <div className="step-body">Per AISC and NORSOK requirements, padeyes must be designed for a lateral (out-of-plane) load equal to at least 5% of the in-plane design load. This accounts for sling angle tolerances and accidental side loading. The OOK calculator applies this check automatically when the code-based design mode is selected.</div>
              </div>
            </div>
          </div>
        </section>


        <hr className="section-sep" />

        {/* GEOMETRY REQUIREMENTS */}
        <section id="geometry">
          <div className="section-label">Dimensional Requirements</div>
          <h2 className="section-title">Padeye Geometry Requirements</h2>
          <div className="section-body">
            <p>Industry standards impose minimum geometric proportions on padeyes to ensure adequate structural performance and safe shackle fit. The OOK calculator performs all geometry checks automatically as part of the design verification.</p>
          </div>
          <div className="geo-table-wrap">
            <table className="geo-table">
              <thead><tr><th>Parameter</th><th>Requirement</th><th>Reason</th></tr></thead>
              <tbody>
                <tr><td>Outer radius (R)</td><td>R ≥ pin hole diameter (d_h)</td><td>Maintains adequate net section around the hole</td></tr>
                <tr><td>Plate thickness at hole</td><td>t_p ≥ 0.75 × shackle jaw width</td><td>Prevents shackle pin from bearing on only part of the jaw</td></tr>
                <tr><td>Pin-to-hole clearance</td><td>≤ 6% of shackle pin diameter</td><td>Limits bending of shackle pin under lateral loads</td></tr>
                <tr><td>Pin hole diameter</td><td>Matches shackle pin + tolerance</td><td>Ensures rated connection; over-large holes reduce bearing area</td></tr>
                <tr><td>Cheek plate OD</td><td>Typically = shackle eye diameter (Ø_E)</td><td>Concentrically machined cheeks maintain full bearing contact</td></tr>
                <tr><td>Eye clearance (C)</td><td>≤ 10% of w_E or 6 mm, whichever is less</td><td>Limits lateral movement of shackle within eye</td></tr>
                <tr><td>Out-of-plane pin ratio</td><td>Pin ≥ 94% of hole (if OOP load &gt; 10%)</td><td>Prevents localised bearing failure under side loading</td></tr>
                <tr><td>Cheek plates (&gt; 2t load)</td><td>Required above 2,000 kg SWL</td><td>Increases bearing area and reduces shackle-pin play</td></tr>
                <tr><td>Weld type</td><td>Full penetration weld mandatory</td><td>Fillet welds only on outside are prohibited — insufficient strength</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <hr className="section-sep" />

        {/* TYPES OF PADEYES */}
        <section id="types">
          <div className="section-label">Configuration Types</div>
          <h2 className="section-title">Types of Padeyes &amp; Lifting Lugs</h2>
          <div className="types-grid">
            <div className="type-card">
              <div className="type-header">
                <div className="type-dot" style={{ background: '#1D4ED8' }}></div>
                <div className="type-name">Standard Symmetric Padeye</div>
              </div>
              <div className="type-body">
                Used where load is applied vertically or at a small, known in-plane angle. The geometry is symmetric about the vertical load axis.
                <ul>
                  <li>Suitable for single-point vertical lifts</li>
                  <li>Consists of main plate + optional cheek plates</li>
                  <li>Most common in structural fabrication</li>
                  <li>Pin hole centred on the eye axis</li>
                </ul>
              </div>
            </div>
            <div className="type-card">
              <div className="type-header">
                <div className="type-dot" style={{ background: '#059669' }}></div>
                <div className="type-name">Asymmetric / Offshore Padeye</div>
              </div>
              <div className="type-body">
                Used in multi-point offshore lifts where the sling acts at a defined angle (typically 30° from vertical). Geometry is offset from the load axis to minimise stress under the design angle.
                <ul>
                  <li>Standard for offshore and subsea module lifting</li>
                  <li>Sling angle typically 30° — custom angles supported</li>
                  <li>Pin hole offset toward the load direction</li>
                  <li>Requires lateral load check (5% of design load)</li>
                </ul>
              </div>
            </div>
            <div className="type-card">
              <div className="type-header">
                <div className="type-dot" style={{ background: '#D97706' }}></div>
                <div className="type-name">Padeye with Stiffening Brackets</div>
              </div>
              <div className="type-body">
                Heavy-duty configuration for dynamic loading or attachment to thin-walled structure. Ring or gusset stiffeners extend from the main plate into the parent structure.
                <ul>
                  <li>Used in offshore container and pipe rack lifting</li>
                  <li>Stiffeners resist radial and lateral forces</li>
                  <li>Required when attaching to CHS (circular hollow section)</li>
                  <li>Design to Germanischer Lloyd IV-6-4 or NORSOK R-002</li>
                </ul>
              </div>
            </div>
            <div className="type-card">
              <div className="type-header">
                <div className="type-dot" style={{ background: '#DC2626' }}></div>
                <div className="type-name">Seafastening Padeye</div>
              </div>
              <div className="type-body">
                Designed for marine transport lashing — the padeye restrains cargo against vessel motions (roll, pitch, heave accelerations) during sea passage.
                <ul>
                  <li>Load direction varies widely — multi-axis checks required</li>
                  <li>Vessel acceleration factors applied (per IMO or project spec)</li>
                  <li>Often designed to Noble Denton 0027/ND guidelines</li>
                  <li>Safety factors typically lower than lifting padeyes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <hr className="section-sep" />

        {/* WELD DESIGN */}
        <section id="weld">
          <div className="section-label">Weld Verification</div>
          <h2 className="section-title">Padeye Weld Design &amp; Requirements</h2>
          <div className="section-body">
            <p>The weld connecting the padeye main plate to the primary structure is a critical element. Industry standards are unanimous: <strong>all padeyes must use full-penetration welds</strong>. Fillet welds applied only to the outside are prohibited in lifting applications.</p>
          </div>
          <div className="weld-grid">
            <div className="weld-card">
              <h4>Full Penetration Weld Requirement</h4>
              <p>All padeyes must be welded with full penetration welds. Where practical, the base of the padeye should be slotted into the structural member to achieve full penetration, providing weld continuity and eliminating notch effects at the weld root.</p>
            </div>
            <div className="weld-card">
              <h4>Base Plate Material (Z-Quality)</h4>
              <p>When welding directly onto horizontal plate with tensile stress in the through-thickness direction, the ground plate must be Z-quality (Z25 or Z35 per EN 10164) to prevent lamellar tearing under the tensile through-thickness loading from the weld.</p>
            </div>
            <div className="weld-card">
              <h4>Effective Weld Length</h4>
              <p>The effective weld length in padeye stress calculations is conservatively limited to 2R (twice the outer eye radius) per standard practice, accounting for weld stress concentration at the toe and non-uniform stress distribution along the base.</p>
            </div>
            <div className="weld-card">
              <h4>Weld Shear Stress Check</h4>
              <p>The OOK calculator verifies weld shear stress per the applicable standard (Eurocode EN 1993-1-8 or AISC), compared against the permissible shear stress for the specified electrode classification (E43, E49) and the base material grade.</p>
            </div>
          </div>
          <div className="stress-grid">
            <div className="stress-card">
              <div className="stress-label">Weld stress formula</div>
              <div className="stress-formula">τ_w = F / (2R × a_w)</div>
              <div className="stress-desc">F = design load, R = outer radius, a_w = throat thickness of weld (= 0.707 × leg length for fillet weld)</div>
            </div>
            <div className="stress-card">
              <div className="stress-label">Permissible weld shear (Eurocode)</div>
              <div className="stress-formula">τ_w,Rd = f_u / (√3 × β_w × γ_M2)</div>
              <div className="stress-desc">f_u = ultimate tensile strength; β_w = correlation factor (0.85 for S355); γ_M2 = 1.25 (partial factor)</div>
            </div>
            <div className="stress-card">
              <div className="stress-label">NDT &amp; Load Testing</div>
              <div className="stress-formula">SWL_test = 2.0 × SWL</div>
              <div className="stress-desc">Post-manufacture load testing at 2× SWL (proof load) is mandatory per most standards before a padeye is placed into service. MPI or PT weld inspection should be performed after load testing.</div>
            </div>
          </div>
        </section>

        <hr className="section-sep" />

        {/* STANDARDS */}
        <section id="standards">
          <div className="section-label">Design Codes</div>
          <h2 className="section-title">Applicable Standards &amp; Design Codes</h2>
          <div className="section-body">
            <p>Padeye design is governed by several international standards depending on application (lifting, seafastening, offshore, structural). The OOK Pad Eye Calculator supports the most widely referenced codes in offshore and structural engineering practice.</p>
          </div>
          <div className="std-table-wrap">
            <table className="std-table">
              <thead>
                <tr><th>Standard</th><th>Title &amp; Scope</th><th>Key Application</th></tr>
              </thead>
              <tbody>
                <tr><td>AISC BTH-1</td><td>Design of Below-the-Hook Lifting Devices — comprehensive lug analysis methodology covering axial, transverse, and oblique loading modes</td><td>Structural &amp; industrial lifting lugs (US)</td></tr>
                <tr><td>DNV-ST-E273</td><td>Lifting Appliances — covers design, fabrication, testing, and certification of offshore lifting equipment including padeyes and trunnions</td><td>Offshore &amp; subsea lifting (international)</td></tr>
                <tr><td>NORSOK R-002</td><td>Lifting Equipment — Norwegian Continental Shelf standard defining design loads, DAF, safety factors, and material requirements for lifting points</td><td>Offshore lifting on NCS (Norway)</td></tr>
                <tr><td>EN 13155</td><td>Cranes — Non-fixed load lifting attachments. Safety requirements and testing for lifting beams, lifting frames, and plate clamps</td><td>General industrial lifting (Europe)</td></tr>
                <tr><td>Noble Denton 0027/ND</td><td>Guidelines for Marine Lifting and Lowering Operations — covers offshore installation, crane vessel operations, and seafastening design loads</td><td>Marine &amp; offshore installation</td></tr>
                <tr><td>Eurocode 3 (EN 1993-1-8)</td><td>Design of Steel Structures — Joints. Pin connection design rules applicable to padeye hole checks and weld design</td><td>Structural engineering (Europe)</td></tr>
                <tr><td>ASME BTH-1</td><td>Design of Below-the-Hook Lifting Devices — ASME edition covering design standards for custom lifting devices fabricated to order</td><td>Industrial lifting (US)</td></tr>
                <tr><td>Germanischer Lloyd IV-6-4</td><td>Rules for Classification — Industrial Services. Used for verification of heavy-duty padeyes with stiffening brackets, particularly in offshore container design</td><td>Offshore containers &amp; heavy-lift</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <hr className="section-sep" />

        {/* FAQ */}
        <section id="faq">
          <div className="section-label">Common Questions</div>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {[
              { q: "What is the difference between a padeye and a lifting lug?", a: "The terms are often used interchangeably. In strict usage, a lifting lug is the broader term for any plate-and-hole device used to transmit load through a pin or shackle connection. A padeye (or padear) is a specific sub-type designed to mate with a standard shackle, where the circular eye geometry and shackle compatibility are explicitly checked. The OOK calculator handles both — the padeye design mode incorporates shackle database compatibility checks that a generic lug analysis does not." },
              { q: "What material should I use for a padeye?", a: "Padeyes are most commonly made from carbon structural steel. S355 (355 MPa yield) per EN 10025 is the standard choice for offshore and marine applications. S275 or A36 (250 MPa) suits lighter duty applications. For heavy offshore lifting (100+ tonnes), S460 or higher grades may be used to reduce plate thickness. Stainless steel may be specified for corrosion-critical environments. When welding to horizontal plate in the through-thickness load direction, the base plate must be Z-quality (Z25 or Z35) to prevent lamellar tearing." },
              { q: "What Dynamic Amplification Factor (DAF) should I use?", a: "DAF accounts for dynamic effects during a lift — inertia, crane hook acceleration, and sea-state motion. Typical values: DAF = 1.05–1.10 for slow, controlled onshore crane lifts; DAF = 1.10–1.15 for standard offshore lifts in calm conditions; DAF = 1.20–1.30 for offshore lifts in moderate sea state; DAF up to 1.35+ for dynamic subsea deployment. Your project specification or the applicable standard (DNV-ST-E273, NORSOK R-002) will prescribe minimum DAF values based on lift weight, crane type, and sea state. The OOK calculator accepts any DAF as an input factor applied to the nominal lift weight." },
              { q: "Is load testing required after manufacturing a padeye?", a: "Yes, in virtually all regulated lifting applications. Post-manufacture proof load testing at 2× SWL (Safe Working Load) is mandatory per DNV, NORSOK, and most national lifting regulations before a padeye is placed into service. The load test procedure should be documented and witnessed. Magnetic Particle Inspection (MPI) or Dye Penetrant Testing (PT) of all welds is typically required after load testing to confirm no fatigue cracking has occurred. A calibrated load cell should be used to verify the actual test load applied." },
              { q: "When are cheek plates required on a padeye?", a: "Cheek plates are circular bearing plates welded concentrically to both faces of the main padeye plate. They are generally required for padeyes with a rated capacity above 2,000 kg (2 tonnes) to reduce the gap between the shackle pin and padeye hole and increase the bearing area. Cheek plate outside diameter is typically machined to match the internal diameter of the shackle eye. Cheek plates improve bearing stress distribution but are not included in tensile or shear capacity calculations — only the main plate is used for those checks." },
              { q: "Can I use the OOK Pad Eye Calculator for seafastening lashings?", a: "Yes. PadEyes are equally applicable to restraint (lashing) and lifting applications. For seafastening, the load direction is typically a combination of horizontal and vertical vessel acceleration forces in multiple directions. Safety factors for lashing padeyes are normally slightly lower than for lifting, since lifting introduces larger dynamic amplification. When using the OOK calculator for seafastening, apply the appropriate vessel motion acceleration factors from the project marine warranty survey (MWS) report or a vessel motions analysis as the input load F, including all relevant load directions." },
              { q: "What size shackle should I use with my padeye?", a: "Shackle size is selected based on the design load, with the shackle rated (WLL) to at least the factored design load. The padeye geometry must then match the selected shackle: the pin hole diameter should be the shackle pin diameter plus minimum clearance (≤ 6% of pin diameter); the plate thickness at the hole must be ≥ 75% of the shackle jaw inside width; and the cheek plate OD should equal the shackle bow inside diameter. The OOK calculator includes a shackle compatibility database — select your shackle model and the calculator checks geometric compatibility automatically." },
              { q: "What weld type is required for padeye-to-structure connections?", a: "Full penetration welds are mandatory for all padeye base connections in lifting and seafastening applications. Fillet welds on the outside of the frame are explicitly prohibited per offshore standards — they provide insufficient strength and create stress concentrations at the weld root under peel loads. Where possible, the padeye base plate should be slotted through the structural member to allow a full-penetration weld from both sides. NDE (MPI or PT) inspection of all welds is required prior to load testing and placing the padeye into service." }
            ].map((item, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open-item' : ''}`}>
                <button className="faq-q" aria-expanded={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <ChevronDown className="chevron" size={16} />
                </button>
                <div className={`faq-a ${openFaq === i ? 'open' : ''}`}>{item.a}</div>
              </div>
            ))}
          </div>
        </section>

        <hr className="section-sep" />

        {/* RELATED TOOLS */}
        <section id="related">
          <div className="section-label">More Engineering Tools</div>
          <h2 className="section-title">Related OOK Calculators</h2>
          <div className="related-grid">
            <a href="https://ook-calculator.com" className="related-card">
              <div className="related-card-title">Bolt Torque &amp; Preload</div>
              <div className="related-card-desc">Calculate installation torque, preload force, and nut factor for bolted joints per ASME B1.1.</div>
              <div className="related-arrow">Open →</div>
            </a>
            <a href="https://ook-calculator.com" className="related-card">
              <div className="related-card-title">Lifting Rigging Calculator</div>
              <div className="related-card-desc">Determine sling loads, tensions, and angles for multi-point lifts with spreader bars.</div>
              <div className="related-arrow">Open →</div>
            </a>
            <a href="https://ook-calculator.com" className="related-card">
              <div className="related-card-title">Weld Stress Calculator</div>
              <div className="related-card-desc">Calculate weld capacity and utilisation for fillet and groove welds per Eurocode and AISC.</div>
              <div className="related-arrow">Open →</div>
            </a>
            <a href="https://ook-calculator.com" className="related-card">
              <div className="related-card-title">Beam Analysis</div>
              <div className="related-card-desc">Shear force, bending moment, and deflection diagrams for simply supported and cantilever beams.</div>
              <div className="related-arrow">Open →</div>
            </a>
          </div>
        </section>

      </main>
    </div>
  );
}